import api from './client'
import type { PaginatedResponse, ResourceResponse } from '@/types/api'
import type { BrainSettings } from '@/types/brainSettings'
import type {
  AdminOverview,
  AdminUser,
  AdminUserProfile,
  AdminUserLanguageStats,
  AdminInvitationCode,
  AdminLanguage,
  AdminWordSummary,
  AdminReading,
  SrsSettings,
  SrsPreview,
  LabPlan,
  LabSubject,
  LabWord,
  EnrichmentRunFeed,
  EnrichmentRunDetailResponse,
  EnrichmentOutcome,
} from '@/types/admin'
import type { SourceBadgeConfig, SourceBadgeDraft } from '@/types/sourceBadge'

function buildParams(page: number, extra?: Record<string, string>): string {
  const params = new URLSearchParams({ page: String(page), ...extra })
  return params.toString()
}

export function fetchAdminOverview() {
  return api.get<ResourceResponse<AdminOverview>>('/admin/overview')
}

export function fetchAdminUsers(page: number) {
  return api.get<PaginatedResponse<AdminUser>>(`/admin/users?${buildParams(page)}`)
}

export function fetchAdminUser(id: number) {
  return api.get<ResourceResponse<AdminUserProfile>>(`/admin/users/${id}`)
}

export function fetchAdminUserLanguageStats(userId: number, languageId: number) {
  return api.get<ResourceResponse<AdminUserLanguageStats>>(`/admin/users/${userId}/languages/${languageId}`)
}

/** A learner's tracked words in one language matching `q`; a blank query lists those due soonest. */
export function searchLearnerWords(userId: number, languageId: number, q: string) {
  const params = new URLSearchParams({ q }).toString()
  return api.get<{ data: LabWord[] }>(`/admin/users/${userId}/languages/${languageId}/words?${params}`)
}

export function fetchAdminInvitationCodes(page: number) {
  return api.get<PaginatedResponse<AdminInvitationCode>>(`/admin/invitation_codes?${buildParams(page)}`)
}

export function createInvitationCode() {
  return api.post<ResourceResponse<AdminInvitationCode>>('/admin/invitation_codes')
}

export function fetchAdminLanguages(page: number) {
  return api.get<PaginatedResponse<AdminLanguage>>(`/admin/languages?${buildParams(page)}`)
}

export function fetchAdminWords(page: number, params?: { q?: string; language_id?: string }) {
  const extra: Record<string, string> = {}
  if (params?.q) extra.q = params.q
  if (params?.language_id) extra.language_id = params.language_id
  return api.get<PaginatedResponse<AdminWordSummary>>(`/admin/words?${buildParams(page, extra)}`)
}

export function fetchAdminReadings(page: number) {
  return api.get<PaginatedResponse<AdminReading>>(`/admin/readings?${buildParams(page)}`)
}

export function deleteAdminUser(id: number) {
  return api.delete(`/admin/users/${id}`)
}

export function deleteAdminWord(id: number) {
  return api.delete(`/admin/words/${id}`)
}

export function createAdminLanguage(data: { name: string; iso_code: string; description?: string }) {
  return api.post('/admin/languages', { language: data })
}

export function deleteAdminLanguage(id: number) {
  return api.delete(`/admin/languages/${id}`)
}

export function deleteAdminReading(id: number) {
  return api.delete(`/admin/readings/${id}`)
}

export function fetchSrsSettings() {
  return api.get<ResourceResponse<SrsSettings>>('/admin/srs_setting')
}

export function updateSrsSettings(settings: SrsSettings) {
  return api.patch<ResourceResponse<SrsSettings>>('/admin/srs_setting', { srs_setting: settings })
}

/** Projects a candidate parameter set through the real scheduler without saving it. */
export function previewSrsSettings(settings: SrsSettings) {
  return api.post<ResourceResponse<SrsPreview>>('/admin/srs_setting/preview', { srs_setting: settings })
}

/** Plays a rating script forward under unsaved settings; nothing is written. */
export function simulateSrs(settings: SrsSettings, subject: LabSubject, ratings: number[]) {
  return api.post<ResourceResponse<LabPlan>>('/admin/srs_simulation', {
    srs_setting: settings,
    subject,
    ratings,
  })
}

/** The admin's own tracked words matching `q`; a blank query lists those due soonest. */
export function searchSrsWords(q: string) {
  return api.get<{ data: LabWord[] }>(`/admin/srs_words?${new URLSearchParams({ q }).toString()}`)
}

/** `known_sources` are the sources the lexicon actually holds, badge or not. */
export function fetchAdminSourceBadges() {
  return api.get<{ data: SourceBadgeConfig[]; known_sources: string[] }>('/admin/source_badges')
}

export function createSourceBadge(badge: SourceBadgeDraft) {
  return api.post<ResourceResponse<SourceBadgeConfig>>('/admin/source_badges', {
    source_badge: badge,
  })
}

export function updateSourceBadge(id: number, badge: Partial<SourceBadgeDraft>) {
  return api.patch<ResourceResponse<SourceBadgeConfig>>(`/admin/source_badges/${id}`, {
    source_badge: badge,
  })
}

export function deleteSourceBadge(id: number) {
  return api.delete(`/admin/source_badges/${id}`)
}

export function fetchEnrichmentRunFeed(page: number) {
  return api.get<EnrichmentRunFeed>(`/admin/enrichment_runs?${buildParams(page)}`)
}

interface EnrichmentRunDetailParams {
  outcome: EnrichmentOutcome | null
  page: number
}

export function fetchEnrichmentRunDetail(id: number, params: EnrichmentRunDetailParams) {
  const extra = params.outcome ? { outcome: params.outcome } : undefined
  return api.get<EnrichmentRunDetailResponse>(
    `/admin/enrichment_runs/${id}?${buildParams(params.page, extra)}`,
  )
}

export function fetchBrainSettings() {
  return api.get<ResourceResponse<BrainSettings>>('/admin/brain_setting')
}

export function updateBrainSettings(settings: BrainSettings) {
  return api.patch<ResourceResponse<BrainSettings>>('/admin/brain_setting', { brain_setting: settings })
}
